import { get as idbGet, set as idbSet } from 'idb-keyval';
import Vue from 'vue';

import services from '../../../core/services';
import store from '../../../core/store';
import {
  IApplicationsService,
  PiivoApplicationLifecycleService,
} from '../../../platform/services/types';
import { debounce } from '../../../utils/debounce';
import { openDb } from '../../../utils/indexedDb';
import { POSTER_APP_NAME } from '../constants/index';
import { CartPoster } from '../types/poster';

/**
 * Name of our IDB database
 */
const DB_NAME = 'piivo';

/**
 * Name of the IDB key-value store
 */
const KV_STORE_NAME = 'poster';

/**
 * Key for the cart in the store
 */
const CART_KEY = 'cart';

/**
 * Admin config for persistence
 */
interface PersistenceConfig {
  enable?: boolean;
}

export class CartStateService implements PiivoApplicationLifecycleService {
  /**
   * IndexedDB database for the cart
   */
  private db: IDBDatabase | null = null;

  /**
   * Local cart state for computed getters/setters
   */
  private state: { cart: Record<string, CartPoster[]> } = Vue.observable({
    cart: {},
  });

  /**
   * Vue instance to watch the local cart state to trigger updates.
   */
  private vm: Vue | null = null;

  /**
   * The poster cart persistence config.
   */
  private persistenceConfig: PersistenceConfig | null | undefined;

  /**
   * If persistent state was already initialized.
   */
  private didInitPersistence = false;

  /**
   * Callback for POSter app mount
   */
  async onApplicationMount(): Promise<void> {
    const config = services
      .getService<IApplicationsService>('applications')
      ?.getAppDeepParameter<PersistenceConfig>(
        POSTER_APP_NAME,
        'screensSettings',
        'creation.cart.persistence',
        true
      );
    if (config instanceof Error) {
      throw config;
    }
    this.persistenceConfig = config;

    if (this.persistenceConfig?.enable) {
      await this.setupPersistence();
    }
  }

  /**
   * Callback for POSter app before destroyed
   */
  public onBeforeApplicationDestroyed() {
    // Remove watchers and other state created in setupPersistence()
    // to free up system resources
    this.vm?.$destroy();
    this.vm = null;
    this.db?.close();
    this.db = null;

    // Reset our local state as well:
    // - allows users' carts to be empty by default
    // - prevents memory leaks
    this.state = Vue.observable({
      cart: {},
    });
    this.persistenceConfig = null;

    this.didInitPersistence = false;
  }

  /**
   * Connects idb-keyval to our database store
   *
   * @param txMode - store transaction mode
   * @param callback - callback to return our store
   * @returns our store
   */
  private useStore<T>(
    txMode: IDBTransactionMode,
    callback: (store: IDBObjectStore) => T | PromiseLike<T>
  ): Promise<T> {
    if (!this.db) {
      throw new Error('CartStateService.setup() not called, database not available');
    }

    return callback(
      this.db.transaction(KV_STORE_NAME, txMode).objectStore(KV_STORE_NAME)
    ) as Promise<T>;
  }

  /**
   * Saves the cart state to storage
   */
  private debouncedSaveState = debounce(
    async () => {
      await idbSet(CART_KEY, this.state.cart, this.useStore.bind(this));
    },
    50, // Debounce enough for a second keystroke
    false
  );

  /**
   * Restores the cart state from storage
   */
  private async restoreState(): Promise<void> {
    this.state.cart = (await idbGet(CART_KEY, this.useStore.bind(this))) ?? {};
  }

  /**
   * Sets the current user's cart
   *
   * @param cart - the cart state
   */
  private setUserCart(cart: CartPoster[]): void {
    const userName = store.getters.user.userName;
    Vue.set(this.state.cart, userName, cart);
  }

  /**
   * Sets up storage and restores the existing cart from storage.
   * Does nothing if called a second time
   */
  private async setupPersistence() {
    if (this.didInitPersistence) {
      // Prevent duplicate setup from external sources
      return;
    }

    this.didInitPersistence = true;

    this.db = await openDb(DB_NAME, 1, {
      onUpgrade: db => {
        if (!db.objectStoreNames.contains(KV_STORE_NAME)) {
          db.createObjectStore(KV_STORE_NAME);
        }
      },
    });

    await this.restoreState();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this;

    // Watch the cart array to update the store's value
    // Even if components use computed getters/setters to retrieve/set the cart value,
    // they only track assignment https://github.com/vuejs/vue/issues/8867#issuecomment-425698957
    // To track operations like Array.push, we need to watch the array itself
    // https://github.com/vuejs/vue/issues/9509#issuecomment-464460414
    this.vm = new Vue({
      created() {
        this.$watch(
          () => service.state,
          () => {
            service.debouncedSaveState();
          },
          { deep: true }
        );
      },
    });
  }

  /**
   * Updates the cart state
   *
   * @param cart - the new cart state
   */
  public set(cart: CartPoster[]): void {
    this.setUserCart(cart);

    if (this.persistenceConfig?.enable) {
      // Do this async
      void this.debouncedSaveState();
    }
  }

  /**
   * @returns the cart state
   */
  public get() {
    const userName = store.getters.user.userName;

    // The value returned by this method must be reactive.
    // Create it if it does not exist
    if (!this.state.cart[userName]) {
      this.setUserCart([]);
    }

    return this.state.cart[userName];
  }
}
